import { useMemo } from "react";
import { useQuery } from "react-query";
import { GeographyType } from "../../components/GeoSelect/GeoTypes";
import {
  CoverageStatus,
  HcpLandscapeRow,
  MarketSummary,
  PlanData,
  PlanFeed,
  PrescribingHabits,
  TopHealthcareProviders,
} from "../../generate";

export type EmailTemplateButtonConfig = {
  type: string;
  labelKey: string;
  includeAttachment?: boolean;
};

export type ProductPrintConfiguration = {
  hcpPrintMessages: boolean;
  hcpEmailMessages: boolean;
  resourceLibrary: boolean;
  useIsPIForEmailMessages: boolean;
  useIsPIForPrintMessages: boolean;
  defaultIsPIEmail: boolean;
  defaultIsPIPrint: boolean;
  printEnabledForFRM: boolean;
  disablePrintQueue?: boolean;
  digitalOnlyFilenameSuffix?: boolean;
  showPrintIconButton?: boolean;
  showEmailIconButton?: boolean;
  showEmailIosButtons?: boolean;
  emailTemplateButtons?: EmailTemplateButtonConfig[];
};

type ProductPayerLandscapeConfiguration = {
  enabled?: boolean;
  visibleColumns?: (keyof PlanFeed)[];
  visibleFrmColumns?: (keyof PlanFeed)[];
  productComparison?: boolean;
  detailedViewProductComp?: TableConfiguration<keyof PlanFeed>;
  indicationFiltering?: boolean;
  indicationColumnMappings?: {
    [key: string]: (keyof PlanFeed)[];
  };
  roleAdditionalColumns?: {
    [role: string]: (keyof PlanFeed)[];
  };
  allIndicationsConfig?: {
    fixedProductDisplay?: string;
    fixedProductValue?: string;
  };
  comparisonTab?: {
    dataKeys?: string[];
    productColumnOverrides?: ProductColumnOverrides;
  };
  showNrxCompTrxToggle?: boolean;
};

type ProductPlanProfileConfiguration = {
  planData?: TableConfiguration<keyof PlanData>;
  hiddenForGeneral?: boolean;
  indicationFiltering?: boolean;
  topTLC: ProductPlanProfileTopHCPConfiguration;
  pharmacyBenefitManagers: boolean;
  hideMarketSummary?: boolean;
  marketSummary?: TableConfiguration<keyof MarketSummary>;
};

type ProductPlanProfileTopHCPConfiguration = {
  dataKeys: (keyof TopHealthcareProviders)[];
  sortTopTLC?: boolean;
  sortTLCDataKey?: keyof TopHealthcareProviders;
  sortTLCDirection?: string;
  hideViewAllButton?: boolean;
  useIndicationInNames?: boolean;
  dataKeysOptions?: Record<string, (keyof TopHealthcareProviders)[]>;
  roleAdditionalColumns?: Record<string, (keyof TopHealthcareProviders)[]>;
};

type ProductHCPLandscapeConfiguration = {
  enabled?: boolean;
  visibleColumns?: (keyof HcpLandscapeRow | "full_name")[];
  visibleFrmColumns?: (keyof HcpLandscapeRow | "full_name")[];
  indicationFiltering?: boolean;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
  targetListFiltering?: boolean;
  indicationColumnMappings?: Record<string, string[]>;
  roleAdditionalColumns?: {
    [role: string]: (keyof HcpLandscapeRow | "full_name")[];
  };
};

type ProductHCPProfileConfiguration = {
  indicationFiltering?: boolean;
  prescribingHabits?: TableConfiguration<keyof PrescribingHabits>;
  prescribingHabitsVisibility?: {
    allowedRoles?: string[];
  };
  hideHabitsTooltip?: boolean;
  hideHabitsTotalMarket?: boolean;
  costAndCoverageMessages: boolean;
  hideCostAndCoverageTooltip?: boolean;
  planInformation?: PlanInformationConfiguration;
};

type TableConfiguration<T> = {
  dataKeys?: T[];
  dataKeysOptions?: Record<string, T[]>;
};

type PlanInformationConfiguration = TableConfiguration<keyof CoverageStatus> & {
  roleAdditionalColumns?: {
    [role: string]: (keyof CoverageStatus)[];
  };
};

type RouteConfig = {
  path?: string;
  label?: string;
  [key: string]: RouteConfig | string | undefined;
};

export type ProductConfiguration = {
  products?: string[];
  allowedDataWindows?: "all" | string[];
  print?: ProductPrintConfiguration;
  payerLandscape?: ProductPayerLandscapeConfiguration;
  planProfile?: ProductPlanProfileConfiguration;
  hcpLandscape?: ProductHCPLandscapeConfiguration;
  hcpProfile?: ProductHCPProfileConfiguration;
  defaultGeographyLevel?: GeographyType | null;
  rootGeographyLevel?: GeographyType | null;
  routeOverrides?: RouteConfig;
  hasHcpAccessAsFrm?: boolean;
  showFootnote?: boolean;
  hasAdtMktTrxTrans?: boolean;
  hidePlanDataTooltip?: boolean;
  hideMarketSummaryTooltip?: boolean;
  hidePlanDataLastRow?: boolean;
  hidePlanDataRight?: boolean;
  hideCompStatusColumnsAsFrm?: boolean;
  howToVideo?: string;
  howToFrmVideo?: string;
  hideHowToVideoFromFrm?: boolean;
  indicationFilters?: { value: string; label: string; disabled?: boolean }[];
  showHcpLandscapeFootnote?: boolean;
  showPayerLandscapeFootnote?: boolean;
};

interface ProductColumnOverrides {
  [productKey: string]: {
    label?: string;
    subLabel?: string;
    width?: number;
    align?: "left" | "right" | "center";
  };
}

const EMPTY_CONFIGURATION: ProductConfiguration = {};
const CONFIGURATION_LOCALE = "en";

const fetchClientConfiguration = async (client: string): Promise<ProductConfiguration> => {
  const version = process.env.REACT_APP_VERSION;
  const url = `/locales/${CONFIGURATION_LOCALE}/product-configuration/${client}.json${version ? `?v=${version}` : ""}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load product configuration for "${client}": ${response.status}`);
  }
  return response.json();
};

export const useClientConfiguration = (client?: string | null): ProductConfiguration => {
  // A client's configuration is a static JSON file fetched by name and cached by
  // React Query under the client key. Until it resolves (or when there is no
  // client) consumers get an empty config and fall back to per-hook defaults.
  const { data } = useQuery(["product-configuration", client], () => fetchClientConfiguration(client as string), {
    enabled: Boolean(client),
    staleTime: Infinity,
    retry: false,
  });
  return data ?? EMPTY_CONFIGURATION;
};

export const useClientAllowedDataWindowsConfiguration = (client?: string | null) => {
  const config = useClientConfiguration(client);
  return config.allowedDataWindows || "all";
};

export const useClientPrintConfiguration = (client?: string | null) => {
  const defaultPrintConfiguration: ProductPrintConfiguration = {
    hcpPrintMessages: true,
    hcpEmailMessages: true,
    resourceLibrary: true,
    useIsPIForEmailMessages: true,
    useIsPIForPrintMessages: true,
    defaultIsPIEmail: true,
    defaultIsPIPrint: true,
    printEnabledForFRM: false,
    showPrintIconButton: false,
  };
  const config = useClientConfiguration(client);
  return config.print || defaultPrintConfiguration;
};

export const useClientPayerLandscapeConfiguration = (client?: string | null) => {
  const defaultPayerLandscapeConfiguration: ProductPayerLandscapeConfiguration = {
    enabled: true,
    productComparison: true,
    indicationFiltering: false,
    detailedViewProductComp: {
      dataKeys: [
        "m_plan_name",
        "lives",
        "cc_status",
        "rel_status",
        "comp_cc_status",
        "comp_rel_status",
        "mkt_trx",
        "prod_trx",
        "mkt_share",
        "share_delta",
        "comp_prod_trx",
      ],
    },
  };
  const config = useClientConfiguration(client);
  return config.payerLandscape || defaultPayerLandscapeConfiguration;
};

export const useClientPayerLandscapeColumnsConfiguration = (client?: string | null, columnsProfileHidden?: boolean) => {
  const defaultPayerLandscapeConfiguration: ProductPayerLandscapeConfiguration = {
    visibleColumns: [
      "m_plan_name",
      "pbm_vendor",
      "lives",
      "pct_bob",
      "cc_status",
      "rel_status",
      "comp_cc_status",
      "comp_rel_status",
      "mkt_trx",
      "prod_trx",
      "mkt_share",
      "share_delta",
      "comp_prod_trx",
      "prod_nrx",
    ],
    visibleFrmColumns: [
      "m_plan_name",
      "pbm_vendor",
      "lives",
      "cc_status",
      "rel_status",
      "comp_cc_status",
      "comp_rel_status",
      "mkt_trx",
      "prod_trx",
      "mkt_share",
      "share_delta",
    ],
  };
  const config = useClientConfiguration(client);

  return columnsProfileHidden
    ? defaultPayerLandscapeConfiguration.visibleFrmColumns
    : config?.payerLandscape?.visibleColumns || defaultPayerLandscapeConfiguration.visibleColumns;
};

export const useClientPayerLandscapeComparisonConfig = (client?: string | null) => {
  const defaultConfig = {
    dataKeys: undefined,
    productColumnOverrides: {} as ProductColumnOverrides,
  };

  const config = useClientConfiguration(client);

  return {
    dataKeys: config.payerLandscape?.comparisonTab?.dataKeys || defaultConfig.dataKeys,
    productColumnOverrides: config.payerLandscape?.comparisonTab?.productColumnOverrides || {},
  };
};

export const useClientPlanProfileConfiguration = (client?: string | null) => {
  const defaultPlanProfileConfiguration: ProductPlanProfileConfiguration = {
    pharmacyBenefitManagers: true,
    hiddenForGeneral: false,
    indicationFiltering: false,
    topTLC: {
      dataKeys: ["topHCPs", "marketShare", "mkt_trx"],
    },
  };
  const config = useClientConfiguration(client);
  return config.planProfile || defaultPlanProfileConfiguration;
};

export const useClientPlanProfileMarketSummaryConfiguration = (client?: string | null) => {
  const defaultClientPlanProfileMarketSummaryConfiguration: TableConfiguration<keyof MarketSummary> = {
    dataKeys: ["coverageStatus", "relativeStatus", "tRx", "marketShare"],
  };
  const config = useClientPlanProfileConfiguration(client);
  return config.marketSummary || defaultClientPlanProfileMarketSummaryConfiguration;
};

export const useClientPlanProfilePlanDataConfiguration = (client?: string | null) => {
  const defaultClientPlanProfilePlanDataConfiguration: TableConfiguration<keyof PlanData> = {
    dataKeys: ["totalLives", "percentBOB", "reportingHcps"],
  };
  const config = useClientPlanProfileConfiguration(client);
  return config.planData || defaultClientPlanProfilePlanDataConfiguration;
};

export const useIndicationBasedPlanDataConfiguration = (
  client: string | undefined,
  indication: string | null | undefined,
) => {
  const existingConfig = useClientPlanProfilePlanDataConfiguration(client);

  if (!existingConfig) return { dataKeys: [] };

  // Check if the client has indication-based options
  if (existingConfig.dataKeysOptions && indication && existingConfig.dataKeysOptions[indication]) {
    return { dataKeys: existingConfig.dataKeysOptions[indication] };
  }

  // Fall back to the original dataKeys
  return { dataKeys: existingConfig.dataKeys || [] };
};

export const useClientHCPProfileConfiguration = (client?: string | null) => {
  const defaultClientHCPProfile: ProductHCPProfileConfiguration = {
    indicationFiltering: false,
    costAndCoverageMessages: true,
    hideHabitsTooltip: false,
  };
  const config = useClientConfiguration(client);

  if (!config.hcpProfile) {
    return defaultClientHCPProfile;
  }

  const mergedConfig: ProductHCPProfileConfiguration = {
    ...defaultClientHCPProfile,
    ...config.hcpProfile,
  };

  return mergedConfig;
};

export const useClientHCPLandscapeConfiguration = (client?: string | null) => {
  const defaultClientHCPLandscape: ProductHCPLandscapeConfiguration = {
    enabled: true,
    indicationFiltering: false,
    sortColumn: undefined,
    sortDirection: "asc",
    targetListFiltering: false,
  };
  const config = useClientConfiguration(client);
  return config.hcpLandscape || defaultClientHCPLandscape;
};

export const useClientHCPProfilePrescribingHabitsConfiguration = (client?: string | null) => {
  const defaultPrescribingHabitsConfiguration: TableConfiguration<keyof PrescribingHabits> = {
    dataKeys: [
      "product",
      "commercialTrx",
      "commercialMarketShare",
      "medicaidTrx",
      "medicaidMarketShare",
      "allChannelsTrx",
      "allChannelsMarketShare",
    ],
  };
  const config = useClientHCPProfileConfiguration(client);
  return config?.prescribingHabits || defaultPrescribingHabitsConfiguration;
};

export const useClientHCPProfilePlanInformationConfiguration = (client?: string | null) => {
  const defaultPlanInformationConfiguration: PlanInformationConfiguration = {
    dataKeys: [
      "m_plan_name",
      "channel",
      "bob_pct_ovrl",
      "bob_pct_chnl",
      "cc_status",
      "rel_status",
      "prod_trx",
      "mkt_trx",
      "prod_share",
    ],
  };
  const config = useClientHCPProfileConfiguration(client);
  return config?.planInformation || defaultPlanInformationConfiguration;
};

export const useIndicationBasedPlanInformationConfiguration = (
  client: string | undefined,
  indication: string | null | undefined,
  role?: string | null,
) => {
  const existingConfig = useClientHCPProfilePlanInformationConfiguration(client);

  if (!existingConfig) return { dataKeys: [] };

  // Get base dataKeys (existing logic)
  let baseDataKeys: string[];

  if (existingConfig.dataKeysOptions && indication && existingConfig.dataKeysOptions[indication]) {
    baseDataKeys = existingConfig.dataKeysOptions[indication];
  } else {
    baseDataKeys = existingConfig.dataKeys || [];
  }

  // Add role-specific columns if they exist
  if (role) {
    const roleAdditionalColumns = existingConfig.roleAdditionalColumns?.[role];
    if (roleAdditionalColumns && roleAdditionalColumns.length > 0) {
      return { dataKeys: [...baseDataKeys, ...roleAdditionalColumns] };
    }
  }

  return { dataKeys: baseDataKeys };
};

export const useClientPlanProfileTopHCPConfiguration = (client?: string | null) => {
  const defaultPlanProfileTopHCPConfiguration: ProductPlanProfileTopHCPConfiguration = {
    dataKeys: [
      "topHCPs",
      "bob_pct_ovrl",
      "prod_trx",
      "mkt_trx",
      "fav_pct",
      "acc_pct",
      "prod_share",
      "chnl_prod_trx",
      "chnl_mkt_trx",
      "chnl_prod_share",
    ],
    sortTopTLC: false,
  };
  const config = useClientPlanProfileConfiguration(client);
  return config?.topTLC || defaultPlanProfileTopHCPConfiguration;
};

export const useIndicationBasedTopHCPConfiguration = (
  client: string | undefined,
  indication: string | null | undefined,
  role?: string | null,
) => {
  const existingConfig = useClientPlanProfileTopHCPConfiguration(client);

  if (!existingConfig) {
    return {
      sortTopTLC: false,
      sortTLCDataKey: undefined,
      sortTLCDirection: undefined,
      dataKeys: [],
      hideViewAllButton: false,
      useIndicationInNames: false,
    };
  }

  let baseDataKeys = existingConfig.dataKeys || [];
  if (existingConfig.dataKeysOptions && indication && existingConfig.dataKeysOptions[indication]) {
    baseDataKeys = existingConfig.dataKeysOptions[indication];
  }

  // Add role-specific columns if they exist
  let finalDataKeys = baseDataKeys;
  if (role) {
    const roleAdditionalColumns = existingConfig.roleAdditionalColumns?.[role];
    if (roleAdditionalColumns && roleAdditionalColumns.length > 0) {
      finalDataKeys = [...baseDataKeys, ...roleAdditionalColumns];
    }
  }

  return {
    ...existingConfig,
    dataKeys: finalDataKeys,
  };
};

export const useClientDetailTabColumnConfiguration = (client?: string | null) => {
  const defaultDetailsConfiguration: TableConfiguration<keyof PlanFeed> = {
    dataKeys: ["m_plan_name", "lives", "cc_status", "rel_status", "mkt_trx", "prod_trx", "mkt_share", "share_delta"],
  };
  const config = useClientPayerLandscapeConfiguration(client);
  return config?.detailedViewProductComp || defaultDetailsConfiguration;
};

export const useClientDefaultGeographyLevelConfiguration = (client?: string | null) => {
  const config = useClientConfiguration(client);
  return config?.defaultGeographyLevel || null;
};

export const useClientRootGeographyLevelConfiguration = (client?: string | null) => {
  const config = useClientConfiguration(client);
  return config?.rootGeographyLevel || null;
};

export const useClientRouteOverrides = (client?: string | null) => {
  const config = useClientConfiguration(client);
  return config?.routeOverrides || null;
};

export const useClientIndicationFiltersconfiguration = (client?: string | null) => {
  const config = useClientConfiguration(client);
  return config?.indicationFilters;
};

export const useClientIndicationColumnConfiguration = (client: string, indication: string): (keyof PlanFeed)[] => {
  const config = useClientConfiguration(client);

  if (!config || !config.payerLandscape) {
    return [];
  }

  // Safely access with proper type checking
  const mappings = config.payerLandscape.indicationColumnMappings || {};

  if (mappings[indication]) {
    return mappings[indication];
  }

  // Fall back to default configuration
  return config.payerLandscape.detailedViewProductComp?.dataKeys || [];
};

export const useClientIndicationAllConfig = (client: string) => {
  const config = useClientConfiguration(client);
  return config?.payerLandscape?.allIndicationsConfig || null;
};

export const usePrescribingHabitsVisibility = (client?: string | null, role?: string | null) => {
  const hcpConfig = useClientHCPProfileConfiguration(client);

  const shouldShowHabits = useMemo(() => {
    const allowedRoles = hcpConfig?.prescribingHabitsVisibility?.allowedRoles || [];

    // If no allowed roles are configured, hide the component
    if (allowedRoles.length === 0) {
      return false;
    }

    // Show only if current role is in allowed roles
    return allowedRoles.includes(String(role));
  }, [hcpConfig, role]);

  return shouldShowHabits;
};
